<template>
  <AppShell
    v-model:current-lang="currentLang"
    variante="produto"
    evitar-banner-mobile
    @lang-change="onLangChange"
  >
    <!-- ============================================================
         HERO — contexto da página (área profissional, pre-reunião)
         ============================================================ -->
    <PageHero
      :logo-src="logoBranco"
      logo-alt="OligoCheck"
      :badge="t.heroBadge"
      badge-variante="azul"
      :subtitulo="t.heroSubtitulo"
    >
      <template #titulo>
        <span class="titulo-resto">{{ t.heroTituloAntes + ' ' }}</span
        ><span class="ds-texto-gradiente">{{ t.heroTituloDestaque }}</span
        ><span class="titulo-resto">{{ ' ' + t.heroTituloDepois }}</span>
      </template>

      <template #ctas>
        <!-- Timeline horizontal do onboarding (3 vídeos + reunião) -->
        <ol class="etapas" :aria-label="t.etapasAria">
          <li class="etapa etapa--atual">
            <span class="etapa-num">01</span>
            <span class="etapa-label">{{ t.etapa1 }}</span>
          </li>
          <li class="etapa">
            <span class="etapa-num">02</span>
            <span class="etapa-label">{{ t.etapa2 }}</span>
          </li>
          <li class="etapa">
            <span class="etapa-num">03</span>
            <span class="etapa-label">{{ t.etapa3 }}</span>
          </li>
          <li class="etapa etapa--final">
            <span class="etapa-num etapa-num-icone" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
            </span>
            <span class="etapa-label">{{ t.etapa4 }}</span>
          </li>
        </ol>
      </template>
    </PageHero>

    <!-- ============================================================
         VÍDEO 1 — O que é o OligoCheck + como são os resultados
         ============================================================ -->
    <SecaoPadrao id="video-1" container="sm">
      <div class="video-bloco">
        <div class="video-numero" aria-hidden="true">01</div>
        <SecaoHeader
          :badge="t.badge1"
          badge-variante="azul"
        >
          <template #titulo>
            {{ t.video1Antes + ' ' }}<span class="ds-texto-gradiente">{{ t.video1Destaque }}</span>{{ ' ' + t.video1Depois }}
          </template>
        </SecaoHeader>

        <div class="video-wrapper">
          <iframe
            src="https://www.youtube.com/embed/6zfzdgGk6ac?rel=0"
            :title="t.video1Titulo"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            loading="lazy"
          ></iframe>
        </div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         VÍDEO 2 — Tecnologia não invasiva
         ============================================================ -->
    <SecaoPadrao id="video-2" variante="alt" container="sm">
      <div class="video-bloco">
        <div class="video-numero" aria-hidden="true">02</div>
        <SecaoHeader
          :badge="t.badge2"
          badge-variante="azul"
        >
          <template #titulo>
            <span class="ds-texto-gradiente">{{ t.video2Destaque }}</span>{{ ' ' + t.video2Depois }}
          </template>
        </SecaoHeader>

        <div class="video-wrapper">
          <iframe
            src="https://www.youtube.com/embed/yH9fwjxD9QA?rel=0"
            :title="t.video2Titulo"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            loading="lazy"
          ></iframe>
        </div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         VÍDEO 3 — Médicos que já usam + documentação
         ============================================================ -->
    <SecaoPadrao id="video-3" container="sm">
      <div class="video-bloco">
        <div class="video-numero" aria-hidden="true">03</div>
        <SecaoHeader
          :badge="t.badge3"
          badge-variante="azul"
        >
          <template #titulo>
            {{ t.video3Antes + ' ' }}<span class="ds-texto-gradiente">{{ t.video3Destaque }}</span>
          </template>
        </SecaoHeader>

        <div class="video-wrapper">
          <iframe
            src="https://www.youtube.com/embed/aQwNIvURyxM?rel=0"
            :title="t.video3Titulo"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            loading="lazy"
          ></iframe>
        </div>

        <!-- Documentos complementares -->
        <div class="docs-bloco">
          <p class="docs-label">{{ t.docsLabel }}</p>

          <div class="docs-grid">
            <a
              class="caminho-card caminho-doc"
              href="https://socheck.nyc3.cdn.digitaloceanspaces.com/imagens/ESTUDIOSOLIGOCHECK.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div class="caminho-icone icone-doc">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/>
                  <path d="M14 2v6h6"/>
                  <path d="M12 18v-6"/>
                  <path d="m9 15 3 3 3-3"/>
                </svg>
              </div>
              <div class="caminho-conteudo">
                <span class="caminho-label">{{ t.doc1Label }}</span>
                <strong class="caminho-titulo">{{ t.doc1Titulo }}</strong>
                <span class="caminho-desc">{{ t.doc1Sub }}</span>
              </div>
              <div class="caminho-seta">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7h10v10"/><path d="M7 17 17 7"/></svg>
              </div>
            </a>

            <a
              class="caminho-card caminho-doc"
              href="https://socheck.nyc3.cdn.digitaloceanspaces.com/imagens/ANALISIS%20EVOLUCION%20MARZO-AGOSTO.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div class="caminho-icone icone-doc">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 3v18h18"/>
                  <path d="m19 9-5 5-4-4-3 3"/>
                </svg>
              </div>
              <div class="caminho-conteudo">
                <span class="caminho-label">{{ t.doc2Label }}</span>
                <strong class="caminho-titulo">{{ t.doc2Titulo }}</strong>
                <span class="caminho-desc">{{ t.doc2Sub }}</span>
              </div>
              <div class="caminho-seta">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 7h10v10"/><path d="M7 17 17 7"/></svg>
              </div>
            </a>
          </div>
        </div>
      </div>
    </SecaoPadrao>

    <!-- ============================================================
         CTA FINAL — próximo passo: agendar reunião com o gestor
         ============================================================ -->
    <SecaoPadrao id="cta-gestor" variante="alt" container="sm">
      <div class="cta-bloco">
        <SecaoHeader
          :badge="t.ctaBadge"
          badge-variante="destaque"
          :titulo-antes="t.ctaTituloAntes"
          :titulo-destaque="t.ctaTituloDestaque"
          :subtitulo="t.ctaSubtitulo"
        />

        <a
          :href="whatsappUrl"
          target="_blank"
          rel="noopener"
          class="caminho-card caminho-cta"
        >
          <div class="caminho-icone icone-cta">
            <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
            </svg>
          </div>
          <div class="caminho-conteudo">
            <span class="caminho-label">{{ t.ctaCardLabel }}</span>
            <strong class="caminho-titulo">{{ t.ctaCardTitulo }}</strong>
            <span class="caminho-desc">{{ t.ctaCardDesc }}</span>
          </div>
          <div class="caminho-seta">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </div>
        </a>

        <p class="cta-selo">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <span>{{ t.ctaSelo }}</span>
        </p>
      </div>
    </SecaoPadrao>

    <template #rodape>
      <RodapeSite marca="OligoCheck" />
    </template>
  </AppShell>
</template>

<script setup>
import { ref, computed } from 'vue'
import AppShell from '@/components/global/layout/AppShell.vue'
import PageHero from '@/components/global/layout/PageHero.vue'
import SecaoPadrao from '@/components/global/secao/SecaoPadrao.vue'
import SecaoHeader from '@/components/global/secao/SecaoHeader.vue'
import RodapeSite from '@/components/global/layout/RodapeSite.vue'
import logoBranco from '@/assets/images/logo-branco.svg'

const currentLang = ref('es')

const onLangChange = () => {
  /* AppShell já controla a escolha manual internamente. */
}

/* ============================================================
   WhatsApp — voltar para a conversa com o gestor.
   A pessoa recebeu este link pelo próprio WhatsApp, então não
   pré-preenchemos nenhuma mensagem: apenas reabrimos a conversa.
   ============================================================ */
const whatsappFone = '34617488404'
const whatsappUrl = `https://api.whatsapp.com/send/?phone=${whatsappFone}`

/* ============================================================
   i18n
   ============================================================ */
const translations = {
  es: {
    heroBadge: 'Área profesional · Pre-reunión',
    heroTituloAntes: 'Vea estos',
    heroTituloDestaque: '3 videos cortos',
    heroTituloDepois: 'antes de reunirse con nuestro gestor especialista',
    heroSubtitulo:
      'Descubra cómo medir más de 40 oligoelementos y el estrés oxidativo en menos de 5 segundos, directamente desde la palma de la mano del paciente.',
    etapasAria: 'Etapas de la pre-reunión',
    etapa1: 'Video 01',
    etapa2: 'Video 02',
    etapa3: 'Video 03',
    etapa4: 'Reunión',

    badge1: 'Video 1 de 3',
    video1Antes: '¿Qué es OligoCheck y',
    video1Destaque: 'cómo se ven los resultados',
    video1Depois: 'de los análisis?',
    video1Titulo: '¿Qué es OligoCheck? Cómo lucen los resultados.',

    badge2: 'Video 2 de 3',
    video2Destaque: 'Tecnología no invasiva',
    video2Depois:
      'para conseguir una analítica completa en 5 segundos.',
    video2Titulo: 'OligoCheck — tecnología no invasiva.',

    badge3: 'Video 3 de 3',
    video3Antes: '¿Qué opinan los médicos que ya usan OligoCheck?',
    video3Destaque: 'Estudios científicos y homologación.',
    video3Titulo: '¿Qué opinan los médicos? Estudios y homologación.',

    docsLabel: 'Documentación complementaria',
    doc1Label: 'PDF · Estudios',
    doc1Titulo: 'Estudios científicos',
    doc1Sub: 'Ver artículos que respaldan OligoCheck.',
    doc2Label: 'PDF · Evolución',
    doc2Titulo: 'Evolución de análisis',
    doc2Sub: 'Comparativa real de marzo a agosto.',

    ctaBadge: 'Siguiente paso',
    ctaTituloAntes: 'Ahora sí,',
    ctaTituloDestaque: 'vuelve a tu WhatsApp',
    ctaSubtitulo:
      'Ya viste los 3 videos y la documentación. Retoma la conversación con tu gestor para avanzar con tu propuesta.',
    ctaCardLabel: 'Continuar la conversación',
    ctaCardTitulo: 'Volver al WhatsApp',
    ctaCardDesc:
      'Tu gestor te está esperando para hablar de los próximos pasos.',
    ctaSelo: 'Conversación segura y sin compromiso'
  },
  pt: {
    heroBadge: 'Área profissional · Pré-reunião',
    heroTituloAntes: 'Assista a estes',
    heroTituloDestaque: '3 vídeos curtos',
    heroTituloDepois: 'antes de se reunir com nosso gestor especialista',
    heroSubtitulo:
      'Descubra como medir mais de 40 oligoelementos e o estresse oxidativo em menos de 5 segundos, diretamente na palma da mão do paciente.',
    etapasAria: 'Etapas da pré-reunião',
    etapa1: 'Vídeo 01',
    etapa2: 'Vídeo 02',
    etapa3: 'Vídeo 03',
    etapa4: 'Reunião',

    badge1: 'Vídeo 1 de 3',
    video1Antes: 'O que é o OligoCheck e',
    video1Destaque: 'como são os resultados',
    video1Depois: 'das análises?',
    video1Titulo: 'O que é o OligoCheck? Como são os resultados.',

    badge2: 'Vídeo 2 de 3',
    video2Destaque: 'Tecnologia não invasiva',
    video2Depois:
      'para conseguir uma análise completa em 5 segundos.',
    video2Titulo: 'OligoCheck — tecnologia não invasiva.',

    badge3: 'Vídeo 3 de 3',
    video3Antes: 'O que dizem os médicos que já usam o OligoCheck?',
    video3Destaque: 'Estudos científicos e homologação.',
    video3Titulo: 'O que dizem os médicos? Estudos e homologação.',

    docsLabel: 'Documentação complementar',
    doc1Label: 'PDF · Estudos',
    doc1Titulo: 'Estudos científicos',
    doc1Sub: 'Veja os artigos que embasam o OligoCheck.',
    doc2Label: 'PDF · Evolução',
    doc2Titulo: 'Evolução de análises',
    doc2Sub: 'Comparativo real de março a agosto.',

    ctaBadge: 'Próximo passo',
    ctaTituloAntes: 'Agora sim,',
    ctaTituloDestaque: 'volte ao seu WhatsApp',
    ctaSubtitulo:
      'Você já viu os 3 vídeos e a documentação. Retome a conversa com seu gestor para seguir com a sua proposta.',
    ctaCardLabel: 'Continuar a conversa',
    ctaCardTitulo: 'Voltar ao WhatsApp',
    ctaCardDesc:
      'Seu gestor está te esperando para falar sobre os próximos passos.',
    ctaSelo: 'Conversa segura e sem compromisso'
  },
  en: {
    heroBadge: 'Professional area · Pre-meeting',
    heroTituloAntes: 'Watch these',
    heroTituloDestaque: '3 short videos',
    heroTituloDepois: 'before meeting our specialist manager',
    heroSubtitulo:
      "Discover how to measure over 40 trace elements and oxidative stress in less than 5 seconds — right from the palm of your patient's hand.",
    etapasAria: 'Pre-meeting steps',
    etapa1: 'Video 01',
    etapa2: 'Video 02',
    etapa3: 'Video 03',
    etapa4: 'Meeting',

    badge1: 'Video 1 of 3',
    video1Antes: 'What is OligoCheck and',
    video1Destaque: 'how do the results look',
    video1Depois: 'from the analyses?',
    video1Titulo: 'What is OligoCheck? How the results look.',

    badge2: 'Video 2 of 3',
    video2Destaque: 'Non-invasive technology',
    video2Depois:
      'to deliver a complete analysis in 5 seconds.',
    video2Titulo: 'OligoCheck — non-invasive technology.',

    badge3: 'Video 3 of 3',
    video3Antes: "What do doctors who already use OligoCheck think?",
    video3Destaque: 'Scientific studies and certification.',
    video3Titulo: 'What doctors think. Studies and certification.',

    docsLabel: 'Supplementary documentation',
    doc1Label: 'PDF · Studies',
    doc1Titulo: 'Scientific studies',
    doc1Sub: 'See the papers that back OligoCheck.',
    doc2Label: 'PDF · Evolution',
    doc2Titulo: 'Analysis evolution',
    doc2Sub: 'Real comparison from March to August.',

    ctaBadge: 'Next step',
    ctaTituloAntes: 'Now,',
    ctaTituloDestaque: 'go back to your WhatsApp',
    ctaSubtitulo:
      "You've watched the 3 videos and the documentation. Head back to the conversation with your manager to move your proposal forward.",
    ctaCardLabel: 'Continue the conversation',
    ctaCardTitulo: 'Back to WhatsApp',
    ctaCardDesc:
      'Your manager is waiting to walk you through the next steps.',
    ctaSelo: 'Secure conversation, no strings attached'
  }
}

const t = computed(() => translations[currentLang.value])

/* Sem useSeo (página privada por link). /profesionales em Disallow no robots.txt */
</script>

<style scoped>
/* ============================================================
   TÍTULO — textos que acompanham o gradiente no hero
   ============================================================ */
.titulo-resto { color: var(--ds-text); }

/* ============================================================
   TIMELINE HORIZONTAL DE ETAPAS (hero)
   Uma "linha do tempo" sutil 01 → 02 → 03 → ✓ Reunião.
   Pura indicação visual (não interativa).
   ============================================================ */
.etapas {
  list-style: none;
  margin: 0 auto;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px 18px;
  max-width: 720px;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-full);
  box-shadow: var(--ds-shadow-lg);
}

.etapa {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12.5px;
  color: var(--ds-text-dim);
  font-family: var(--ds-font-bold);
  letter-spacing: 0.4px;
  position: relative;
}

.etapa + .etapa::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 50%;
  width: 4px;
  height: 1px;
  background: var(--ds-border);
  transform: translateY(-50%);
}

.etapa-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--ds-border);
  color: var(--ds-text-faint);
  font-size: 11.5px;
  font-family: var(--ds-font-bold);
}

.etapa--atual .etapa-num {
  background: var(--ds-accent-soft);
  border-color: var(--ds-accent-border);
  color: var(--ds-accent);
  box-shadow: 0 0 0 4px rgba(20, 203, 240, 0.08);
}

.etapa--atual .etapa-label {
  color: var(--ds-text);
}

.etapa--final .etapa-num {
  background: rgba(26, 175, 149, 0.12);
  border-color: rgba(26, 175, 149, 0.35);
  color: #1aaf95;
}

.etapa--final .etapa-label {
  color: #7ddbc8;
}

/* ============================================================
   BLOCO DE VÍDEO — container interno de cada SecaoPadrao
   ============================================================ */
.video-bloco {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

/* Número gigante decorativo ao fundo */
.video-numero {
  position: absolute;
  top: -28px;
  right: -4px;
  font-family: var(--ds-font-bold);
  font-size: clamp(110px, 18vw, 180px);
  line-height: 1;
  letter-spacing: -0.06em;
  background: linear-gradient(135deg, rgba(20, 203, 240, 0.14), rgba(26, 175, 149, 0.06));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  pointer-events: none;
  user-select: none;
  z-index: 0;
}

/* Garante que o cabeçalho fica acima do número decorativo */
.video-bloco :deep(.section-header) {
  position: relative;
  z-index: 1;
}

/* Badge das seções de vídeo alinhada à esquerda (título permanece centralizado).
   Override apenas para o cabeçalho dos blocos de vídeo. */
.video-bloco :deep(.section-header .ds-badge-pill) {
  align-self: flex-start;
  margin-left: 0;
  margin-right: auto;
}

/* ============================================================
   VIDEO WRAPPER (mesmo padrão das outras páginas)
   ============================================================ */
.video-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--ds-r-xl);
  overflow: hidden;
  background: #000;
  border: 1px solid var(--ds-border);
  box-shadow:
    0 30px 80px -20px rgba(0, 0, 0, 0.65),
    0 0 0 1px rgba(20, 203, 240, 0.1);
  z-index: 1;
}

.video-wrapper iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

/* ============================================================
   BLOCO DE DOCUMENTOS (abaixo do vídeo 3)
   ============================================================ */
.docs-bloco {
  margin-top: var(--ds-s-8);
  padding-top: var(--ds-s-7);
  border-top: 1px solid var(--ds-border-subtle);
}

.docs-label {
  font-family: var(--ds-font-bold);
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--ds-text-dim);
  margin: 0 0 var(--ds-s-4);
  text-align: center;
}

.docs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: var(--ds-s-3);
}

/* ============================================================
   CAMINHO-CARD (mesmo padrão usado em LinkBio/Erro)
   ============================================================ */
.caminho-card {
  display: flex;
  align-items: center;
  gap: var(--ds-s-4);
  text-align: left;
  background: var(--ds-surface-2);
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-r-lg);
  padding: 18px 20px;
  text-decoration: none;
  color: var(--ds-text);
  transition: all var(--ds-t-smooth);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.caminho-card::before {
  content: '';
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity var(--ds-t-base);
  pointer-events: none;
}

.caminho-card:hover::before { opacity: 1; }

.caminho-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--ds-shadow-lg);
}

.caminho-doc::before { background: linear-gradient(135deg, rgba(212, 168, 83, 0.14), transparent 60%); }
.caminho-cta::before { background: linear-gradient(135deg, rgba(37, 211, 102, 0.16), transparent 60%); }

.caminho-doc:hover { border-color: rgba(212, 168, 83, 0.45); }
.caminho-cta:hover { border-color: rgba(37, 211, 102, 0.5); }

.caminho-icone {
  min-width: 52px;
  height: 52px;
  border-radius: var(--ds-r-md);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  color: #fff;
}

.icone-doc {
  background: linear-gradient(135deg, #d4a853, #a87d33);
  box-shadow: 0 10px 28px -6px rgba(212, 168, 83, 0.45);
}

.icone-cta {
  background: var(--ds-gradient-success);
  box-shadow: var(--ds-shadow-glow-success);
}

.caminho-conteudo {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  position: relative;
  z-index: 1;
  min-width: 0;
}

.caminho-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--ds-text-dim);
  font-family: var(--ds-font-bold);
}

.caminho-titulo {
  font-family: var(--ds-font-bold);
  font-size: 16px;
  color: var(--ds-text);
  line-height: 1.25;
}

.caminho-desc {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.45;
  margin-top: 2px;
}

.caminho-seta {
  color: var(--ds-text-faint);
  transition: all var(--ds-t-base);
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.caminho-card:hover .caminho-seta {
  color: var(--ds-text);
  transform: translateX(4px);
}

/* ============================================================
   CTA FINAL — centralização e selo
   ============================================================ */
.cta-bloco {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  max-width: 560px;
  margin: 0 auto;
}

.caminho-cta {
  padding: 22px;
}

.caminho-cta .caminho-icone {
  min-width: 56px;
  height: 56px;
}

.caminho-cta .caminho-titulo {
  font-size: 17px;
}

.caminho-cta .caminho-desc {
  font-size: 13px;
}

.cta-selo {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: var(--ds-s-5) auto 0;
  padding: 8px 14px;
  border: 1px solid var(--ds-border-subtle);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.02);
  color: var(--ds-text-faint);
  font-size: 12px;
  letter-spacing: 0.3px;
  width: fit-content;
  max-width: 100%;
}

.cta-selo svg { flex-shrink: 0; }

/* ============================================================
   RESPONSIVO
   ============================================================ */
@media (max-width: 720px) {
  .etapas {
    padding: 14px 16px;
    gap: 8px 14px;
    border-radius: var(--ds-r-xl);
  }

  .etapa { font-size: 11.5px; }
  .etapa-num { width: 24px; height: 24px; font-size: 11px; }

  .video-numero {
    font-size: clamp(80px, 28vw, 130px);
    top: -18px;
  }

  .video-wrapper { border-radius: var(--ds-r-lg); }

  .caminho-card { padding: 16px; gap: var(--ds-s-3); }
  .caminho-icone { min-width: 46px; height: 46px; }
  .caminho-titulo { font-size: 15px; }
  .caminho-desc { font-size: 12px; }

  .caminho-cta { padding: 18px; }
  .caminho-cta .caminho-icone { min-width: 50px; height: 50px; }
  .caminho-cta .caminho-titulo { font-size: 16px; }

  .docs-bloco { margin-top: var(--ds-s-6); padding-top: var(--ds-s-5); }
  .docs-grid { grid-template-columns: 1fr; }

  .cta-selo { font-size: 11.5px; padding: 7px 12px; }
}
</style>
